import React, { useEffect, useState, useRef, useCallback } from 'react';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ChatIcon from '@mui/icons-material/Chat';
import { 
  Box, 
  Button, 
  Card, 
  Container, 
  Divider, 
  Paper, 
  Stack, 
  Typography, 
  useTheme,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Popper,
  Fade,
  IconButton,
  Chip,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Image from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import TextSizeContext2 from './MarkdownRenderer';
import MobileReportEditor from './MobileReportEditor';
import GuidelinesSidebar from './GuidelinesSidebar';
import PromptsSidebar from './PromptsSidebar';
import DesktopAIChatInterface from './DesktopAIChatInterface';
import ReportEditModeContent from './ReportEditModeContent';
import AuthService from '../../../services/AuthService';
import { useDebounce } from '../../../hooks/useDebounce';
import { getReportById, updateReport, deleteReport, previewDeleteReport, DeletePreviewData } from "../../../services/reportAPI";
import TourGuide from "../../../../features/components/TourGuide";
import { Step } from "react-joyride";

// Update the ReportImage interface (unchanged)
interface ReportImage {
  url: string;
  label: string;
  isHighlighted: boolean;
}

// Update the ReportData interface to include status and isConfirmed
interface ReportData {
  id?: string;
  caseId: string;
  patientId?: string;
  title: string;
  content: string;
  status?: string;
  isConfirmed?: boolean;
  images: ReportImage[];
  createdAt: string;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const ReportPreview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: caseIdParam, reportId: reportIdParam } = useParams<{ id: string, reportId: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldStartInEditMode = searchParams.get('edit') === 'true';
  
  // Get caseId from route params or query params (for /report-preview/:reportId route)
  const caseId = caseIdParam || searchParams.get('caseId') || undefined;
  const reportId = reportIdParam || undefined;
  // Get patientId from query params if provided
  const patientIdFromQuery = searchParams.get('patientId') || undefined;
  

  // Add these new state variables after the existing useState declarations (around line 130)
const [isAutoFormatting, setIsAutoFormatting] = useState(false);
const [isRefining, setIsRefining] = useState(false);
const [selectionPopperAnchor, setSelectionPopperAnchor] = useState<HTMLElement | null>(null);
const [refinementDialogOpen, setRefinementDialogOpen] = useState(false);
const [refinementInstruction, setRefinementInstruction] = useState('');
const [currentSelectedText, setCurrentSelectedText] = useState('');
const [chatMessage, setChatMessage] = useState('');
const [isChatProcessing, setIsChatProcessing] = useState(false);
const [translateMenuAnchor, setTranslateMenuAnchor] = useState<null | HTMLElement>(null);
const [chatHistory, setChatHistory] = useState<Array<{ role: 'user' | 'assistant', message: string }>>([]);
const [comingSoonOpen, setComingSoonOpen] = useState(false);
const [comingSoonFeature, setComingSoonFeature] = useState('');
const [isGuidelinesSidebarOpen, setIsGuidelinesSidebarOpen] = useState(false);
const [isPromptsSidebarOpen, setIsPromptsSidebarOpen] = useState(false);

  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ReportImage | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePreview, setDeletePreview] = useState<DeletePreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // PDF blob state for email sending
  const [lastGeneratedPDF, setLastGeneratedPDF] = useState<{ blob: Blob; filename: string } | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Add state for the tour
  const [isTourRunning, setIsTourRunning] = useState(false);
  // Add state to track if we should enable edit mode for the tour
  const [tourEditModeEnabled, setTourEditModeEnabled] = useState(false);
  
  const [report, setReport] = useState<ReportData>({
    caseId: caseId || '',
    patientId: patientIdFromQuery,
    title: 'Case Report',
    content: 'No report content available. Please refresh and check your internet access',
    images: [],
    createdAt: new Date().toISOString(),
    status: 'draft',
    isConfirmed: false
  });
  
  const [isEditing, setIsEditing] = useState(shouldStartInEditMode);
  const [editableContent, setEditableContent] = useState('');
  const [contentHistory, setContentHistory] = useState<string[]>([]);
  const [redoHistory, setRedoHistory] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [lastSavedContent, setLastSavedContent] = useState('');
  const [translatedLanguages, setTranslatedLanguages] = useState<Set<string>>(new Set());
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExportingWord, setIsExportingWord] = useState(false);

  // Debounce the content changes for autosave
  const debouncedContent = useDebounce(editableContent, 2000);

  const isNewReport = !reportId;
  const reportTitle = isNewReport ? "Create New Report" : "Edit Report";

  // Utility function to remove "Attached Images" sections from markdown
  const cleanMarkdownContent = (content: string): string => {
    if (!content) return content;
    
    let cleaned = content;
    
    // Remove <br/> variations of Attached Images sections
    // Pattern: <br/>**Attached Images**:<br/> or similar variations
    const brImagePattern = /<br\/?>\s*\*\*Attached\s+Images\*\*:?\s*<br\/?>([\s\S]*?)(?=\n\n|$)/gi;
    cleaned = cleaned.replace(brImagePattern, '');
    
    // Remove markdown heading versions: ### Attached Images or **Attached Images**
    const headingPattern = /(\n*)#{1,3}\s+Attached\s+Images.*?(\n*)/gi;
    cleaned = cleaned.replace(headingPattern, '');
    
    const boldPattern = /(\n*)\*\*Attached\s+Images\*\*:?.*?(\n*)/gi;
    cleaned = cleaned.replace(boldPattern, '');
    
    // Remove all markdown image syntax: ![alt text](url)
    const imagePattern = /!\[.*?\]\(.*?\)/g;
    cleaned = cleaned.replace(imagePattern, '');
    
    // Clean up multiple consecutive newlines (more than 2)
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    // Trim leading/trailing whitespace
    cleaned = cleaned.trim();
    
    return cleaned;
  };

// Export function
  // Load report data either from localStorage for new reports or from API for existing reports
  useEffect(() => {
    // Replace the content setting part in fetchReportData

const fetchReportData = async () => {
  try {
    if (reportId) {
      // If we have a reportId, fetch the report from the API
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      if (!token) throw new Error("No access token available");
      
      const reportData = await getReportById(token, reportId);
      
      // Extract images from markdown content using regex
      const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
      const extractedImages: ReportImage[] = [];
      let match;
      let contentWithoutImages = reportData.markdownContent;
      const imagesToRemove: string[] = [];
      
      // First pass - collect all images
      while ((match = imageRegex.exec(reportData.markdownContent)) !== null) {
        const fullMatch = match[0]; // The entire ![label](url) string
        const label = match[1] || 'Unnamed Image';
        const url = match[2];
        
        // Save the full match for removal later
        imagesToRemove.push(fullMatch);
        
        // Only add if it's a unique URL
        if (!extractedImages.some(img => img.url === url)) {
          extractedImages.push({
            url,
            label,
            isHighlighted: false
          });
        }
      }
      
      // Remove image sections from content
      // Look for patterns like "### Attached Images" or "**Attached Images**"
      const imageHeaderPattern = /(\n*)(#{1,3}\s+Attached\s+Images|\*\*Attached\s+Images\*\*:?)(.*?)(\n*)/gi;
      contentWithoutImages = contentWithoutImages.replace(imageHeaderPattern, '');
      
      // Now remove individual images
      imagesToRemove.forEach(img => {
        contentWithoutImages = contentWithoutImages.replace(img, '');
      });
      
      // Clean up multiple consecutive newlines
      contentWithoutImages = contentWithoutImages.replace(/\n{3,}/g, '\n\n');
      
      setReport((prevReport) => ({
        ...prevReport,
        caseId: reportData.caseId,
        patientId: reportData.patientId || 'N/A',
        content: contentWithoutImages, // Use content without images for display
        status: reportData.status,
        isConfirmed: reportData.isConfirmed,
        createdAt: reportData.createdAt || new Date().toISOString(),
        id: reportData.id,
        images: extractedImages // Add extracted images to report
      }));
      
      // Keep original content with images for editing
      setEditableContent(reportData.markdownContent);
      setLastSavedContent(reportData.markdownContent);
      // Initialize history with the original content
      setContentHistory([reportData.markdownContent]);
    } else {
      // For new reports, use the original flow without extracting images
      const storedReport = localStorage.getItem('tempReport');
      if (storedReport) {
        try {
          const parsedReport = JSON.parse(storedReport);
          setReport({
            ...parsedReport,
            patientId: parsedReport.patientId || 'N/A',
          });
          setEditableContent(parsedReport.content);
          setLastSavedContent(parsedReport.content);
          // Initialize history with the original content
          setContentHistory([parsedReport.content]);
        } catch (error) {
          console.error('Error parsing report data:', error);
          showAlert('Error loading report data', 'error');
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch report:', error);
    showAlert('Failed to load report', 'error');
  }
};

    fetchReportData();
  }, [reportId, caseId]);

  

  // Auto-markdown handler
const handleAutoMarkdown = async () => {
  if (!editableContent.trim()) {
    showAlert('No content to format', 'warning');
    return;
  }

  try {
    setIsAutoFormatting(true);
    
    const authService = new AuthService();
    const token = await authService.getAccessToken();
    
    if (!token) throw new Error("No access token available");
    
    // Clean the content before sending to API
    const cleanedContent = cleanMarkdownContent(editableContent);
    
    const response = await fetch(`${API_URL}/reporting/auto-markdown`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        content: cleanedContent
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to format content: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Save current content to history
    setContentHistory(prevHistory => [...prevHistory, editableContent]);
    
    // Update content with formatted version
    setEditableContent(data.formattedContent);
    
    showAlert('Content formatted successfully!', 'success');
    
  } catch (error) {
    console.error('Auto-format error:', error);
    showAlert(error instanceof Error ? error.message : 'Failed to format content', 'error');
  } finally {
    setIsAutoFormatting(false);
  }
};

// Handle text selection in contentEditable div
const handleTextSelection = useCallback(() => {
  // Don't update selection if refinement dialog is open
  if (refinementDialogOpen) return;
  
  const selection = window.getSelection();
  
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    // No selection, hide popper
    setSelectionPopperAnchor(null);
    setCurrentSelectedText('');
    return;
  }
  
  const range = selection.getRangeAt(0);
  const selectedText = selection.toString();
  
  if (!selectedText.trim()) {
    setSelectionPopperAnchor(null);
    setCurrentSelectedText('');
    return;
  }
  
  setCurrentSelectedText(selectedText);
  
  // Get the bounding rectangle for the selection
  const rect = range.getBoundingClientRect();
  
  // Create a virtual element at the selection end
  const virtualElement = {
    getBoundingClientRect: () => ({
      top: rect.top,
      left: rect.right,
      bottom: rect.bottom,
      right: rect.right,
      width: 0,
      height: rect.height,
      x: rect.right,
      y: rect.top,
      toJSON: () => ({})
    })
  };
  setSelectionPopperAnchor(virtualElement as any);
}, [refinementDialogOpen]);

// Handle content refinement
const handleRefineContent = async () => {
  if (!refinementInstruction.trim()) {
    showAlert('Please provide refinement instructions', 'warning');
    return;
  }
  
  if (!currentSelectedText.trim()) {
    showAlert('No text selected', 'warning');
    return;
  }
  
  try {
    setIsRefining(true);
    setRefinementDialogOpen(false);
    
    const authService = new AuthService();
    const token = await authService.getAccessToken();
    
    if (!token) throw new Error("No access token available");
    
    // Clean the content before sending to API
    const cleanedContext = cleanMarkdownContent(editableContent);
    
    const response = await fetch(`${API_URL}/reporting/refine-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        selectedText: currentSelectedText,
        instruction: refinementInstruction,
        fullContext: cleanedContext
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to refine content: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Save current content to history
    setContentHistory(prevHistory => [...prevHistory, editableContent]);
    
    // Replace selected text with refined version in markdown
    const beforeText = editableContent.substring(0, editableContent.indexOf(currentSelectedText));
    const afterText = editableContent.substring(editableContent.indexOf(currentSelectedText) + currentSelectedText.length);
    const newContent = beforeText + data.refinedText + afterText;
    
    setEditableContent(newContent);
    
    showAlert('Content refined successfully!', 'success');
    setRefinementInstruction('');
    setSelectionPopperAnchor(null);
    setCurrentSelectedText('');
    
  } catch (error) {
    console.error('Content refinement error:', error);
    showAlert(error instanceof Error ? error.message : 'Failed to refine content', 'error');
  } finally {
    setIsRefining(false);
  }
};

  // Handle chat-based AI refinement
  const handleChatSubmit = async () => {
    if (!chatMessage.trim()) return;
    
    // Add user message to history
    const userMessage = chatMessage;
    setChatHistory(prev => [...prev, { role: 'user', message: userMessage }]);
    setChatMessage('');
    
    setIsChatProcessing(true);
    
    try {
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      if (!token) {
        showAlert('Authentication required', 'error');
        setChatHistory(prev => prev.slice(0, -1)); // Remove user message on error
        return;
      }

      // Clean the content before sending to API
      const cleanedContent = cleanMarkdownContent(editableContent);

      const response = await fetch(`${API_URL}/reporting/refine-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          selectedText: cleanedContent, // Send cleaned content
          instruction: userMessage
        })
      });

      if (!response.ok) {
        throw new Error('AI refinement failed');
      }

      const data = await response.json();
      
      // Save current state to history
      setContentHistory(prevHistory => [...prevHistory, editableContent]);
      
      // Update content with AI response
      setEditableContent(data.refinedText);
      
      // Add AI response to chat history
      setChatHistory(prev => [...prev, { role: 'assistant', message: 'Content updated successfully!' }]);
      
      showAlert('Content updated successfully!', 'success');
      
    } catch (error) {
      console.error('Chat refinement error:', error);
      showAlert(error instanceof Error ? error.message : 'Failed to process request', 'error');
      setChatHistory(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setIsChatProcessing(false);
    }
  };

// Open refinement dialog
const handleOpenRefinementDialog = () => {
  setRefinementDialogOpen(true);
  setSelectionPopperAnchor(null);
};

// Close refinement dialog
const handleCloseRefinementDialog = () => {
  setRefinementDialogOpen(false);
  setRefinementInstruction('');
};



  // Set up text selection listener for contentEditable
  useEffect(() => {
    if (!isEditing || !contentRef.current) return;

    const handleSelectionChange = () => {
      handleTextSelection();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, handleTextSelection]);

  // Handle autosave
  const handleAutosave = useCallback(async () => {
    if (!isEditing || !debouncedContent || debouncedContent === lastSavedContent) return;
    
    try {
      setIsSaving(true);
      
      // Save to localStorage
      const updatedReport = {
        ...report,
        content: debouncedContent
      };
      
      localStorage.setItem('tempReport', JSON.stringify(updatedReport));
      
      // If we have a reportId, also save to backend
      if (reportId) {
        const authService = new AuthService();
        const token = await authService.getAccessToken();
        
        if (token) {
          const reportData = {
            caseId: report.caseId,
            patientId: report.patientId || '',
            markdownContent: debouncedContent,
            status: report.status || "draft",
            isConfirmed: report.isConfirmed || false,
          };
          
          await updateReport(token, reportId, reportData);
        }
      }
      
      setLastSavedContent(debouncedContent);
      setHasUnsavedChanges(false);
      
      // Don't show alert for autosave, just update the status indicator
      console.log('Content autosaved');
    } catch (error) {
      console.error('Autosave failed:', error);
      // Don't show error alert for autosave failures
    } finally {
      setIsSaving(false);
    }
  }, [isEditing, debouncedContent, lastSavedContent, report, reportId]);

  // Autosave functionality
  useEffect(() => {
    if (isEditing && debouncedContent && debouncedContent !== lastSavedContent) {
      handleAutosave();
    }
  }, [debouncedContent, isEditing, lastSavedContent, handleAutosave]);

  // Display alert messages
  const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setAlertState({
      open: true,
      message,
      severity
    });
  };

  // Handle alert close
  const handleAlertClose = () => {
    setAlertState({
      ...alertState,
      open: false
    });
  };

  // Handle edit action
  const handleEdit = () => {
    // Toggle edit mode
    setIsEditing(!isEditing);
    if (!isEditing) {
      // When entering edit mode, initialize editable content
      setEditableContent(report.content);
    } else {
      // When exiting edit mode, save changes
      saveChanges();
    }
  };
  
  // Save changes to the report
  const saveChanges = () => {
    try {
      const updatedReport = {
        ...report,
        content: editableContent
      };
      
      setReport(updatedReport);
      setLastSavedContent(editableContent);
      setHasUnsavedChanges(false); // Reset unsaved changes flag
      localStorage.setItem('tempReport', JSON.stringify(updatedReport));
      showAlert('Changes saved successfully. Don\'t forget to hit the Update button.', 'success');
    } catch (error) {
      console.error('Error saving changes:', error);
      showAlert('Failed to save changes', 'error');
    }
  };
  
  // API URL and translation functions
  const API_URL = process.env.REACT_APP_API_URL;

  // Function to translate text using the API
  const translateText = async (text: string, targetLanguage: string) => {
    try {
      // Call the translation API
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      console.log(`Sending translation request for text length: ${text.length}`);
      
      const response = await fetch(`${API_URL}/reporting/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          text: text,
          target_language: targetLanguage,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Translation API error: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.translatedText;
    } catch (error) {
      console.error('Translation API call failed:', error);
      throw error;
    }
  };

  // Handle discard action
  const handleDiscard = () => {
    // If in editing mode, ask for confirmation
    if (isEditing && editableContent !== report.content) {
      if (window.confirm('Discard all changes?')) {
        setIsEditing(false);
        console.log("", caseId)
        // Remove from localStorage and navigate back to the case details view
        localStorage.removeItem('tempReport');
        navigate(`/cases/${caseId||report.caseId}/details?tab=details&patientId=${report.patientId || ''}`); // Navigate to details tab with version control
      }
    } else {
      // Remove from localStorage and navigate back to the case details view
      localStorage.removeItem('tempReport');
      navigate(`/cases/${caseId||report.caseId}/details?tab=details&patientId=${report.patientId || ''}`);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = async () => {
    if (!reportId) return;
    
    try {
      setIsLoadingPreview(true);
      setDeleteDialogOpen(true);
      
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (!token) {
        showAlert('No access token available', 'error');
        setDeleteDialogOpen(false);
        return;
      }
      
      // Fetch preview of what will be deleted
      const preview = await previewDeleteReport(token, reportId);
      setDeletePreview(preview);
      
    } catch (error) {
      console.error('Failed to load delete preview:', error);
      showAlert('Failed to load deletion details. Please try again.', 'error');
      setDeleteDialogOpen(false);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletePreview(null);
  };

  // Handle report deletion
  const handleDeleteReport = async () => {
    if (!reportId) return;

    try {
      setIsDeleting(true);
      
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (!token) throw new Error("No access token available");
      
      // Call delete API
      await deleteReport(token, reportId);
      
      // Remove from localStorage
      localStorage.removeItem('tempReport');
      
      // Show success message
      showAlert('Report deleted successfully', 'success');
      
      // Close dialog
      setDeleteDialogOpen(false);
      
      // Navigate back to case details view after a short delay to show the success message
      setTimeout(() => {
        navigate(`/cases/${caseId || report.caseId}/details?tab=details&patientId=${report.patientId || ''}`);
      }, 1000);
      
    } catch (error) {
      console.error('Failed to delete report:', error);
      showAlert('Failed to delete report. Please try again.', 'error');
      setIsDeleting(false);
    }
  };

  // Handle send email with PDF attachment
  const handleSendEmail = async () => {
    if (!lastGeneratedPDF) {
      showAlert('Please generate the report first by clicking "Print Report"', 'warning');
      return;
    }

    try {
      setIsSendingEmail(true);

      // Get patient email or use placeholder
      const recipientEmail = '';
      const subject = encodeURIComponent(`Dermatology Report - ${report.title || 'Case Report'}`);
      const body = encodeURIComponent(
        `Dear Patient,\n\n` +
        `Please find attached your dermatology report.\n\n` +
        `Report Details:\n` +
        `- Report ID: ${reportId || report.id || 'N/A'}\n` +
        `- Case ID: ${report.caseId}\n` +
        `- Date: ${new Date().toLocaleDateString()}\n\n` +
        `If you have any questions, please don't hesitate to contact us.\n\n` +
        `Best regards,\n` +
        ``
      );

      // For desktop platforms, we can use the File System Access API to save the file
      // and then open the default email client with mailto:
      if ('showSaveFilePicker' in window) {
        try {
          // Save file using File System Access API
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: lastGeneratedPDF.filename,
            types: [{
              description: 'PDF Files',
              accept: { 'application/pdf': ['.pdf'] },
            }],
          });

          const writable = await handle.createWritable();
          await writable.write(lastGeneratedPDF.blob);
          await writable.close();

          // Open email client with pre-filled information
          window.location.href = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
          
          showAlert('PDF saved! Opening email client... Please attach the saved file manually.', 'info');
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            throw err;
          }
          // User cancelled the save dialog
          return;
        }
      } else {
        // Fallback for browsers that don't support File System Access API
        // Download the file and open email client
        const url = window.URL.createObjectURL(lastGeneratedPDF.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = lastGeneratedPDF.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Open email client
        setTimeout(() => {
          window.location.href = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
        }, 500);
        
        showAlert('PDF downloaded! Opening email client... Please attach the downloaded file.', 'info');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      showAlert('Failed to open email client. Please try again.', 'error');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handler for content changes to record history
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleContentChange = useCallback((newContent: string) => {
    // Don't add to history if content is the same
    if (newContent !== editableContent) {
      // Save the current content to history before updating
      // Limit history to 20 entries to prevent memory issues
      setContentHistory(prevHistory => {
        const newHistory = [...prevHistory, editableContent];
        return newHistory.length > 20 ? newHistory.slice(-20) : newHistory;
      });
      
      setEditableContent(newContent);
      // Mark as having unsaved changes
      setHasUnsavedChanges(true);
    }
  }, [editableContent]);
  
  // Undo function that restores the previous content state
  const handleUndo = useCallback(() => {
    if (contentHistory.length > 0) {
      const previousContent = contentHistory[contentHistory.length - 1];
      
      // Save the current content to redo history
      setRedoHistory(prevRedo => [editableContent, ...prevRedo]);
      
      // Restore previous content
      setEditableContent(previousContent);
      
      // Remove the last entry from history
      setContentHistory(prevHistory => prevHistory.slice(0, -1));
    }
  }, [contentHistory, editableContent]);

  // Redo function that restores the next content state
  const handleRedo = useCallback(() => {
    if (redoHistory.length > 0) {
      const nextContent = redoHistory[0];
      
      // Save the current content to undo history
      setContentHistory(prevHistory => [...prevHistory, editableContent]);
      
      // Restore the next content
      setEditableContent(nextContent);
      
      // Remove the first entry from redo history
      setRedoHistory(prevRedo => prevRedo.slice(1));
    }
  }, [redoHistory, editableContent]);

  // Handle translation function
  const handleTranslate = async (targetLanguage: string) => {
    if (translatedLanguages.has(targetLanguage)) {
      showAlert(`Content already translated to ${targetLanguage}`, 'info');
      return;
    }

    try {
      // Show loading state
      setIsTranslating(true);
      
      // Save current content to history before translation
      setContentHistory(prevHistory => [...prevHistory, editableContent]);
      
      const translatedText = await translateText(editableContent, targetLanguage);
      
      // Update the editable content with the translated text
      setEditableContent(translatedText);

      // Mark the language as translated
      setTranslatedLanguages(prev => new Set(prev).add(targetLanguage));
      
      showAlert('Translation completed', 'success');
    } catch (error) {
      console.error('Translation error:', error);
      showAlert('Translation failed. Please try again.', 'error');
    } finally {
      // Hide loading state
      setIsTranslating(false);
    }
  };

  // Format selected text based on format type
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormatText = useCallback((formatType: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

    const selectedText = selection.toString();
    if (!selectedText) return;

    let formattedText = selectedText;
    
    // Save current content to history
    setContentHistory(prevHistory => [...prevHistory, editableContent]);
    
    switch (formatType) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'h1':
        formattedText = `# ${selectedText}`;
        break;
      case 'h2':
        formattedText = `## ${selectedText}`;
        break;
      case 'h3':
        formattedText = `### ${selectedText}`;
        break;
      case 'bullet':
        formattedText = selectedText
          .split('\n')
          .map((line: string) => `- ${line}`)
          .join('\n');
        break;
      case 'number':
        formattedText = selectedText
          .split('\n')
          .map((line: string, index: number) => `${index + 1}. ${line}`)
          .join('\n');
        break;
      case 'diagnosis':
        formattedText = `## Diagnosis\n\n${selectedText}`;
        break;
      case 'observations':
        formattedText = `## Clinical Observations\n\n${selectedText}`;
        break;
      case 'treatment':
        formattedText = `## Recommended Treatment\n\n${selectedText}`;
        break;
      default:
        return;
    }
    
    // Replace selection in markdown
    const beforeText = editableContent.substring(0, editableContent.indexOf(selectedText));
    const afterText = editableContent.substring(editableContent.indexOf(selectedText) + selectedText.length);
    const newContent = beforeText + formattedText + afterText;
    
    setEditableContent(newContent);
  }, [editableContent]);

  // Handle finalize action - now handles both create and update
  const handleFinalize = async () => {
    if (isEditing) {
      saveChanges();
      setIsEditing(false);
    }

    try {
      setIsSaving(true);

      const authService = new AuthService();
      const token = await authService.getAccessToken();

      // Clean content before sending to API to prevent duplicates
      const cleanedContent = cleanMarkdownContent(editableContent);

      const reportData = {
        caseId: report.caseId,
        patientId: report.patientId || '',
        markdownContent: report.images && report.images.length > 0
          ? cleanedContent + "<br/>**Attached Images**:<br/>" + report.images.map(img => `![${img.label}](${img.url})`).join('\n')
          : cleanedContent,
        status: "finalized",
        isConfirmed: true,
      };

      let response;
      // Check both URL param reportId and state report.id (for newly created reports)
      const effectiveReportId = reportId || report.id;
      
      if (effectiveReportId) {
        const token = await authService.getAccessToken();
        if (!token) throw new Error("No access token available");
        // Update existing report - ensure reportId is a string
        console.log(`Updating report with ID: ${effectiveReportId}`);
        response = await updateReport(token, effectiveReportId as string, reportData);
        showAlert('Report updated successfully!', 'success');
      } else {
        // Create new report
        console.log('Creating new report');
        response = await fetch(`${API_URL}/reporting/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(reportData),
        });

        if (!response.ok) {
          throw new Error(`Failed to create report: ${response.statusText}`);
        }
        
        // Parse the response to get the newly created report ID
        const newReportData = await response.json();
        if (newReportData && newReportData.id) {
          // Update the URL to include the new report ID without refreshing the page
          window.history.pushState({}, '', `/cases/${report.caseId}/report/${newReportData.id}`);
          // Update reportId in the component state
          // This is needed to change the "Finalize Report" to "Update Report"
          setReport(prev => ({
            ...prev,
            id: newReportData.id,
            status: "finalized",
            isConfirmed: true
          }));
        }
        
        showAlert('Report created and finalized successfully!', 'success');
      }

      // Clear local storage since we no longer need the temporary report
      localStorage.removeItem('tempReport');
      
      // No navigation - stay on the current page
      // Set the report status to finalized and confirmed
      setReport(prev => ({
        ...prev,
        status: "finalized",
        isConfirmed: true
      }));
      
    } catch (error) {
      console.error('Failed to finalize report:', error);
      showAlert(`Failed to ${reportId || report.id ? 'update' : 'create'} report. Please try again.`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Keyboard shortcuts removed for stability

  // Handle print functionality - refactored to use PrintService and PrintPreviewRenderer
  // const handlePrint = useCallback(async () => {
  //   // Start by showing the printing preparation indicator
  //   setIsPrinting(true);
    
  //   try {
  //     // Format current date nicely using PrintService
  //     const formattedDate = PrintService.formatDate();
      
  //     // Generate the HTML content for the print preview using our component
  //     const printHTML = generatePrintPreviewHTML({
  //       title: report.title,
  //       reportId,
  //       caseId: report.caseId,
  //       patientId: report.patientId,
  //       content: isEditing ? editableContent : report.content,
  //       images: report.images,
  //       createdAt: report.createdAt,
  //       formattedDate,
  //       companyName: "Noetiv-sKin", 
  //       confidentialityNotice: "This report is confidential medical information. Please handle accordingly."
  //     });
      
  //     // Open the print preview window
  //     await PrintService.openPrintPreview(printHTML);
      
  //   } catch (error) {
  //     console.error('Print error:', error);
  //     showAlert(error instanceof Error ? error.message : 'Failed to prepare print preview', 'error');
  //   } finally {
  //     // Hide the printing indicator
  //     setIsPrinting(false);
  //   }
  // }, [report.title, reportId, report.caseId, report.patientId, report.content, report.images, isEditing, editableContent]);


  const handlePrint = useCallback(async () => {
  // Start by showing the printing preparation indicator
  setIsPrinting(true);
  
  try {
    const authService = new AuthService();
    const token = await authService.getAccessToken();
    
    if (!token) throw new Error("No access token available");
    
    // Prepare the report data to send to the backend
    const reportData = {
      reportId: reportId || report.id,
      caseId: report.caseId,
      patientId: report.patientId || 'N/A',
      title: report.title,
      content: isEditing ? editableContent : report.content,
      status: report.status || 'draft',
      isConfirmed: report.isConfirmed || false,
      images: report.images || [],
      createdAt: report.createdAt,
      companyName: "Noetiv-sKin",
      confidentialityNotice: "This report is confidential medical information. Please handle accordingly."
    };
    
    // Call the backend PDF generation endpoint
    const response = await fetch(`${API_URL}/reports/print-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(reportData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || `Failed to generate PDF: ${response.statusText}`);
    }
    
    // Get the PDF blob from the response
    const blob = await response.blob();
    const filename = `dermatology_report_${reportId || 'draft'}_${new Date().getTime()}.pdf`;
    
    // Store the PDF blob for email sending
    setLastGeneratedPDF({ blob, filename });
    
    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    showAlert('PDF generated successfully! You can now send it via email.', 'success');
    
  } catch (error) {
    console.error('Print error:', error);
    showAlert(error instanceof Error ? error.message : 'Failed to generate PDF', 'error');
  } finally {
    // Hide the printing indicator
    setIsPrinting(false);
  }
}, [report.title, reportId, report.id, report.caseId, report.patientId, report.content, report.images, report.status, report.isConfirmed, report.createdAt, isEditing, editableContent, API_URL]);

  // Handle Word export functionality
  const handleExportToWord = useCallback(async () => {
    setIsExportingWord(true);
    
    try {
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Clean content before sending
      const cleanedContent = cleanMarkdownContent(isEditing ? editableContent : report.content);

      // Prepare report data
      const reportData = {
        reportId: reportId || report.id || `temp_${Date.now()}`,
        caseId: report.caseId,
        patientId: report.patientId,
        title: report.title,
        content: cleanedContent,
        status: report.status || 'draft',
        isConfirmed: report.isConfirmed || false,
        images: report.images || [],
        createdAt: report.createdAt,
        companyName: 'Noetiv',
        confidentialityNotice: 'This report is confidential medical information. Please handle accordingly.'
      };

      // Call the backend Word export endpoint
      const response = await fetch(`${API_URL}/reports/export-word`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(reportData),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate Word document: ${response.statusText}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dermatology_report_${reportData.reportId}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showAlert('Word document exported successfully!', 'success');
      
    } catch (error) {
      console.error('Word export error:', error);
      showAlert(error instanceof Error ? error.message : 'Failed to export to Word', 'error');
    } finally {
      setIsExportingWord(false);
    }
  }, [report.title, reportId, report.id, report.caseId, report.patientId, report.content, report.images, report.status, report.isConfirmed, report.createdAt, isEditing, editableContent, API_URL]);

  // Modify handleTourStart to enable edit mode if needed for the tour
  const handleTourStart = () => {
    // Always set tour to running when the button is clicked
    setIsTourRunning(true);
    // Set flag to enable edit mode during tour if not already in edit mode
    if (!isEditing) {
      setTourEditModeEnabled(true);
      // Enable edit mode to make sure editor elements are rendered
      setIsEditing(true);
      // Use current content as editable content
      setEditableContent(report.content);
    }
  };

  const handleTourSkip = () => {
    setIsTourRunning(false);
    // If we enabled edit mode for the tour but user skips, revert to view mode
    if (tourEditModeEnabled) {
      setTourEditModeEnabled(false);
      setIsEditing(false);
    }
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    // If we enabled edit mode for the tour, revert to view mode
    if (tourEditModeEnabled) {
      setTourEditModeEnabled(false);
      setIsEditing(false);
    }
    // Store that the tour was completed, but don't prevent rerunning
    localStorage.setItem('report_finalize_tour_completed', 'true');
  };

  // Define dynamic tour steps that adapt to the UI state
  const getTourSteps = (): Step[] => {
    const steps: Step[] = [
      {
        target: '.report-header',
        content: 'Welcome to the Report Editor. Here you can review and finalize your dermatology case report before sharing it. Press Next and then the X Cancel Button on the upper right cornet to continu the tour',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: '.report-content',
        content: 'This is where your report content is displayed. The system automatically formats your text and includes content from the case.',
        placement: 'top',
      }
    ];

    // Always include edit button step
    steps.push({
      target: '.edit-button',
      content: 'Click here to edit your report. You can format text, add sections, and make any necessary changes.',
      placement: 'bottom',
    });

    // Only include editor steps if in edit mode
    if (isEditing) {
      steps.push({
        target: '.editor-area',
        content: 'When in edit mode, you can type your report here. Use the formatting toolbar to add headings, lists, and other markdown formatting.',
        placement: 'top',
      });
      
      steps.push({
        target: '.preview-area',
        content: 'See a live preview of how your report will look as you type.',
        placement: 'right',
      });
    }
    
    // Check if images section exists before adding its step
    if (report.images && report.images.length > 0) {
      steps.push({
        target: '.images-section',
        content: 'Images from your case are automatically included in your report. They will be attached when the report is finalized.',
        placement: 'top',
      });
    }
    
    // Always include action buttons steps
    steps.push({
      target: '.print-button',
      content: 'You can print your report at any time to generate a professional PDF document.',
      placement: 'left',
    });
    
    // Only include finalize button step if not in edit mode
    if (!tourEditModeEnabled) {
      steps.push({
        target: '.finalize-button',
        content: 'When your report is complete, click here to finalize it. This will save the report and make it available in your reports list.',
        placement: 'left',
      });
    }
    
    return steps;
  };

  // Get dynamic tour steps based on current state
  const tourSteps = getTourSteps();

  // Image viewer handlers
  const handleImageClick = (image: ReportImage, index: number) => {
    setSelectedImage(image);
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  const handleCloseImageViewer = useCallback(() => {
    setImageViewerOpen(false);
    setSelectedImage(null);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  }, []);

  const handleZoomIn = useCallback(() => {
    setImageZoom(prev => Math.min(prev + 0.5, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setImageZoom(prev => Math.max(prev - 0.5, 0.5));
  }, []);

  const handleResetZoom = useCallback(() => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  }, []);

  const handleDownloadImage = useCallback(() => {
    if (!selectedImage) return;
    
    // Open image in a new window/tab
    window.open(selectedImage.url, '_blank', 'noopener,noreferrer');
    showAlert('Image opened in new window', 'success');
  }, [selectedImage]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      // Scroll up - zoom in
      handleZoomIn();
    } else {
      // Scroll down - zoom out
      handleZoomOut();
    }
  };

  const handleNextImage = useCallback(() => {
    if (selectedImageIndex < report.images.length - 1) {
      const nextIndex = selectedImageIndex + 1;
      setSelectedImage(report.images[nextIndex]);
      setSelectedImageIndex(nextIndex);
      setImageZoom(1);
      setImagePan({ x: 0, y: 0 });
    }
  }, [selectedImageIndex, report.images]);

  const handlePreviousImage = useCallback(() => {
    if (selectedImageIndex > 0) {
      const prevIndex = selectedImageIndex - 1;
      setSelectedImage(report.images[prevIndex]);
      setSelectedImageIndex(prevIndex);
      setImageZoom(1);
      setImagePan({ x: 0, y: 0 });
    }
  }, [selectedImageIndex, report.images]);

  // Image viewer keyboard controls
  useEffect(() => {
    if (!imageViewerOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleCloseImageViewer();
          break;
        case 'ArrowLeft':
          if (selectedImageIndex > 0) handlePreviousImage();
          break;
        case 'ArrowRight':
          if (selectedImageIndex < report.images.length - 1) handleNextImage();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case '0':
          handleResetZoom();
          break;
        case 'd':
        case 'D':
          handleDownloadImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageViewerOpen, selectedImageIndex, report.images.length, handleCloseImageViewer, handlePreviousImage, handleNextImage, handleZoomIn, handleZoomOut, handleResetZoom, handleDownloadImage]);

   return (
    <>
      {/* Global CSS override for full-screen edit mode */}
      {isEditing && (
        <style>
          {`
            /* Override parent layout constraints when in edit mode */
            .MuiContainer-root,
            .MuiBox-root,
            main,
            .main-content,
            .content-wrapper,
            .layout-content,
            [class*="layout"],
            [class*="content"],
            [class*="container"] {
              max-width: 100% !important;
              padding-left: 10 !important;
              padding-right: 10 !important;
              margin-left: 10 !important;
              margin-right: 10 !important;
            }
            
            /* Ensure the report preview takes full width */
            .report-preview-fullscreen {
              position: fixed !important;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100vh !important;
              max-width: 100vw !important;
              z-index: 1200 !important;
              background-color: ${theme.palette.background.default} !important;
              overflow: hidden !important;
              margin: 0 !important;
              padding: 0 !important;
              border-radius: 0 !important;
            }
            
            /* Hide sidebar and other navigation when editing */
            .sidebar,
            .navigation,
            .nav-drawer,
            .MuiDrawer-root:not(.guidelines-drawer):not(.finalization-drawer),
            aside,
            nav:not(.report-nav) {
              display: none !important;
            }
            
            /* Ensure guidelines drawer is visible and properly positioned */
            .guidelines-drawer {
              display: block !important;
              z-index: 1300 !important;
            }
            
            /* Ensure finalization drawer is visible and properly positioned */
            .finalization-drawer {
              display: block !important;
              z-index: 1300 !important;
            }
            
            /* Ensure body doesn't scroll */
            body {
              overflow: hidden !important;
            }
          `}
        </style>
      )}

      <Box
        className={isEditing ? 'report-preview-fullscreen' : ''}
        sx={{
          width: '100%',
          height: isEditing ? '100vh' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: isEditing ? 'hidden' : 'auto',
        }}
      >
        {/* Add Tour Guide component with dynamic steps */}
        <TourGuide
          steps={tourSteps}
          tourId="report-finalize"
          run={isTourRunning}
          continuous={true}
          showSkipButton={true}
          showProgress={true}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
          onFinish={handleTourComplete}
        />

        {/* Header Bar - visible in edit mode */}
         {/* Header Bar - visible in edit mode */}
        {isEditing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              flexShrink: 0,
            }}
            className="report-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                {reportTitle}
              </Typography>
              <Tooltip title="Click to copy Case ID" arrow>
                <Chip
                  icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                  label={`Case: ${report.caseId}`}
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(report.caseId);
                    showAlert('Case ID copied to clipboard', 'success');
                  }}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Only show saving status if there are changes */}
              {isSaving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Saving...</Typography>
                </Box>
              ) : hasUnsavedChanges ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutorenewIcon fontSize="small" color="warning" />
                  <Typography variant="caption" color="text.secondary">Unsaved changes</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" color="success" />
                  <Typography variant="caption" color="text.secondary"></Typography>
                </Box>
              )}
              {/* CANCEL CHANGES BUTTON  */}
              <IconButton
                // variant="outlined"
                size="small"
                onClick={() => {
                  // Check if there are unsaved changes
                  if (hasUnsavedChanges) {
                    if (window.confirm('Discard unsaved changes?')) {
                      // Revert to original content and exit edit mode
                      setEditableContent(report.content);
                      setIsEditing(false);
                      setHasUnsavedChanges(false);
                    }
                  } else {
                    // No changes, just exit edit mode
                    setIsEditing(false);
                  }
                }}
                // startIcon={}
              ><CloseIcon/></IconButton>
              {/* SAVE CHANGES bUTTON */}
              <IconButton
                // variant="contained"
                size="small"
                onClick={() => { 
                  saveChanges(); 
                  setIsEditing(false);
                  setHasUnsavedChanges(false);
                }}
                // startIcon={}
                color="success"
              ><SaveIcon/></IconButton>
            </Box>
          </Box>
        )}

        {/* Main Content - Conditional rendering based on edit mode */}
        {isEditing ? (
          /* FULL-SCREEN EDIT MODE */
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            {/* Translation overlay - covers entire edit mode interface */}
            {isTranslating && (
              <Backdrop
                open={true}
                sx={{
                  position: 'absolute',
                  zIndex: 1500,
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress color="inherit" />
                  <Typography sx={{ mt: 2 }}>Translating content...</Typography>
                </Box>
              </Backdrop>
            )}

            {/* Auto-formatting overlay - covers entire edit mode interface */}
            {isAutoFormatting && (
              <Backdrop
                open={true}
                sx={{
                  position: 'absolute',
                  zIndex: 1500,
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress color="inherit" />
                  <Typography sx={{ mt: 2 }}>AI is formatting your document...</Typography>
                </Box>
              </Backdrop>
            )}

            {/* Centered Rendered Markdown Preview with Overlays */}
            <ReportEditModeContent
              editableContent={editableContent}
              isTranslating={isTranslating}
              isAutoFormatting={isAutoFormatting}
              cleanMarkdownContent={cleanMarkdownContent}
            />

            {/* State-of-the-Art AI Chat Interface - Desktop: Fixed at bottom, Mobile: Drawer */}
            <DesktopAIChatInterface
              chatMessage={chatMessage}
              setChatMessage={setChatMessage}
              isChatProcessing={isChatProcessing}
              handleChatSubmit={handleChatSubmit}
              chatHistory={chatHistory}
              handleAutoMarkdown={handleAutoMarkdown}
              isAutoFormatting={isAutoFormatting}
              isTranslating={isTranslating}
              translateMenuAnchor={translateMenuAnchor}
              setTranslateMenuAnchor={setTranslateMenuAnchor}
              handleTranslate={handleTranslate}
              translatedLanguages={translatedLanguages}
              handleUndo={handleUndo}
              handleRedo={handleRedo}
              canUndo={contentHistory.length > 0}
              canRedo={redoHistory.length > 0}
              setComingSoonOpen={setComingSoonOpen}
              setComingSoonFeature={setComingSoonFeature}
              setIsGuidelinesSidebarOpen={setIsGuidelinesSidebarOpen}
              setIsPromptsSidebarOpen={setIsPromptsSidebarOpen}
            />

            {/* Mobile Report Editor */}
            {isEditing && (
              <MobileReportEditor
                chatMessage={chatMessage}
                setChatMessage={setChatMessage}
                isChatProcessing={isChatProcessing}
                handleChatSubmit={handleChatSubmit}
                saveChanges={() => {
                  saveChanges();
                  setIsEditing(false);
                }}
                openTranslateMenu={(e) => setTranslateMenuAnchor(e.currentTarget)}
                handleAutoMarkdown={handleAutoMarkdown}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                canUndo={contentHistory.length > 0}
                canRedo={redoHistory.length > 0}
                handlePatientReportPrompt={() => {
                  setChatMessage('Adjust the tone to a finalized institutional clinical report in first-person plural, preserving the original content, structure, and section order without modification, eliminating third-person or AI-related language');
                }}
                handleAdvisoryNotePrompt={() => {
                  setChatMessage('Include an appropriate medical advisory as a note stating that this report is for informational purposes only and does not substitute a comprehensive in-person medical examination by a qualified healthcare professional.');
                }}
              />
            )}
          </Box>
        ) : (
          /* VIEW MODE - Apple-like SOTA Layout */
          <Container 
            maxWidth={false} 
            sx={{ 
              my: 2, 
              px: { xs: 3, sm: 4, md: 5 },
              width: '100%', 
              maxWidth: '100%',
            }}
          >
            <Paper 
              elevation={3} 
              sx={{
                borderRadius: 3,
                mb: 3,
                backgroundColor: theme.palette.background.paper,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              {/* Header Section - Apple Style */}
              <Box 
                sx={{
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(30,30,35,0.95) 0%, rgba(25,25,30,0.98) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,250,252,0.98) 100%)',
                  backdropFilter: 'blur(20px)',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  px: { xs: 3, md: 4 },
                  py: 3,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography 
                      variant="h4" 
                      component="h1" 
                      sx={{ 
                        fontWeight: 700,
                        letterSpacing: '-0.02em',
                        mb: 1,
                      }}
                    >
                      {reportTitle}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Tooltip title="Click to copy Case ID" arrow>
                        <Chip 
                          icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                          label={`Case: ${report.caseId.substring(0, 12)}...`} 
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(report.caseId);
                            showAlert('Case ID copied to clipboard!', 'success');
                          }}
                          sx={{ 
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        />
                      </Tooltip>
                      {report.patientId && (
                        <Tooltip title="Click to copy Patient ID" arrow>
                          <Chip 
                            icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                            label={`Patient: ${report.patientId.substring(0, 12)}...`} 
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              navigator.clipboard.writeText(report.patientId || '');
                              showAlert('Patient ID copied to clipboard!', 'success');
                            }}
                            sx={{ 
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                      {reportId && (
                        <Tooltip title="Click to copy Report ID" arrow>
                          <Chip 
                            icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                            label={`Report: ${reportId.substring(0, 12)}...`} 
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              navigator.clipboard.writeText(reportId);
                              showAlert('Report ID copied to clipboard!', 'success');
                            }}
                            sx={{ 
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                  
                  <Button
                    variant="contained"
                    startIcon={<HelpOutlineIcon />}
                    onClick={handleTourStart}
                    size="small"
                    sx={{
                      backgroundColor: "#e91e63",
                      color: "white",
                      '&:hover': {
                        backgroundColor: "#d81b60",
                      },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Guide
                  </Button>
                </Stack>
              </Box>

              {/* Action Buttons Bar - Prominent Top Position */}
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: 2.5,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(0,0,0,0.2)'
                    : 'rgba(0,0,0,0.02)',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack 
                  direction="row" 
                  spacing={2} 
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {(reportId || report.id) && (
                      <Button 
                        variant="outlined" 
                        color="error" 
                        startIcon={<DeleteIcon />} 
                        onClick={handleOpenDeleteDialog}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                          },
                        }}
                      >
                        Delete
                      </Button>
                    )}
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      startIcon={<CloseIcon />} 
                      onClick={handleDiscard}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                        },
                      }}
                    >
                      {reportId || report.id ? 'Back' : 'Discard'}
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button 
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />} 
                      onClick={tourEditModeEnabled ? () => {} : handleEdit}
                      className="edit-button"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Edit
                    </Button>
                    
                    {(report.isConfirmed || report.status === "finalized" || report.id || reportId) && (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          onClick={handlePrint}
                          disabled={isPrinting}
                          className="print-button"
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isPrinting ? "Preparing..." : "Print"}
                        </Button>
                        <Button
                          variant="outlined"
                          color="info"
                          startIcon={<DescriptionIcon />}
                          onClick={handleExportToWord}
                          disabled={isExportingWord}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isExportingWord ? "Exporting..." : "Export Word"}
                        </Button>
                        <Button
                          variant="outlined"
                          color="success"
                          startIcon={<EmailIcon />}
                          onClick={handleSendEmail}
                          disabled={isSendingEmail || !lastGeneratedPDF}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderWidth: 2,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {isSendingEmail ? "Sending..." : "Email"}
                        </Button>
                      </>
                    )}
                    
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      onClick={handleFinalize}
                      disabled={isSaving}
                      className="finalize-button"
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        boxShadow: 3,
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 5,
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {isSaving 
                        ? (report.id || reportId ? "Updating..." : "Finalizing...") 
                        : (report.id || reportId ? "Update" : "Finalize")}
                    </Button>
                  </Box>
                </Stack>
              </Box>

              {/* Attached Images Section - Prominently Displayed */}
              {report.images && report.images.length > 0 && (
                <Box 
                  sx={{ 
                    px: { xs: 3, md: 4 },
                    py: 3,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(0,0,0,0.1)'
                      : 'rgba(0,0,0,0.01)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                  className="images-section"
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Image sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                    Attached Images ({report.images.length})
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 2.5,
                      overflowX: 'auto',
                      pb: 2,
                      '&::-webkit-scrollbar': {
                        height: 10,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)'
                          : 'rgba(0,0,0,0.03)',
                        borderRadius: 5,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.2)'
                          : 'rgba(0,0,0,0.2)',
                        borderRadius: 5,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.3)'
                            : 'rgba(0,0,0,0.3)',
                        },
                      }
                    }}
                  >
                    {report.images.map((image, index) => (
                      <Card
                        key={index}
                        elevation={3}
                        onClick={() => handleImageClick(image, index)}
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          flexShrink: 0,
                          width: 180,
                          height: 220,
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.03)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 12px 32px rgba(0,0,0,0.5)'
                              : '0 12px 32px rgba(0,0,0,0.15)',
                          },
                          border: image.isHighlighted ? 
                            `3px solid ${theme.palette.primary.main}` : 
                            `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box sx={{ height: 140, overflow: 'hidden', position: 'relative' }}>
                          <img
                            src={image.url}
                            alt={image.label}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              backgroundColor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                        <Box 
                          sx={{ 
                            p: 1.5,
                            backgroundColor: theme.palette.background.paper,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            height: 80,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            align="center" 
                            sx={{ 
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.3,
                            }}
                          >
                            {image.label}
                          </Typography>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
              
              <Divider />
              <Divider />
              
              {/* Report Content */}
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                }}
              >
                <Card 
                  variant="outlined" 
                  sx={{ 
                    p: { xs: 3, md: 4 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.default,
                    maxHeight: '70vh',
                    overflow: 'auto',
                    width: '100%',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                  className="report-content"
                >
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                    }}
                  >
                    {report.title}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ 
                    height: { md: '60vh' }, 
                    overflowY: 'auto',
                    px: 1,
                    wordWrap: 'break-word',
                    '&::-webkit-scrollbar': {
                      width: 10,
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.03)',
                      borderRadius: 5,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.2)'
                        : 'rgba(0,0,0,0.2)',
                      borderRadius: 5,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.3)'
                          : 'rgba(0,0,0,0.3)',
                      },
                    },
                  }}>
                    <TextSizeContext2 context={cleanMarkdownContent(report.content)} />
                  </Box>
                </Card>
              </Box>
            </Paper>
          </Container>
        )}
      </Box>

      {/* Selection Popper for inline content refinement */}
      <Popper
        open={Boolean(selectionPopperAnchor) && isEditing}
        anchorEl={selectionPopperAnchor}
        placement="top"
        transition
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={8}
              sx={{
                p: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                borderRadius: 2,
              }}
            >
              <Tooltip title="Refine with AI">
                <IconButton
                  size="small"
                  onClick={handleOpenRefinementDialog}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <AutoFixHighIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" sx={{ px: 1 }}>
                Refine with AI
              </Typography>
            </Paper>
          </Fade>
        )}
      </Popper>

      {/* Refinement Instruction Dialog */}
      <Dialog
        open={refinementDialogOpen}
        onClose={handleCloseRefinementDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon color="primary" />
            <Typography variant="h6">Refine Content with AI</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Text:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: theme.palette.background.default,
                maxHeight: '150px',
                overflowY: 'auto',
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {currentSelectedText}
              </Typography>
            </Paper>
          </Box>
          <TextField
            autoFocus
            label="Refinement Instructions"
            placeholder="E.g., Make it more concise, improve clarity, add medical terminology..."
            multiline
            rows={4}
            fullWidth
            value={refinementInstruction}
            onChange={(e) => setRefinementInstruction(e.target.value)}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Describe how you want to refine the selected text.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRefinementDialog}>Cancel</Button>
          <Button
            onClick={handleRefineContent}
            variant="contained"
            disabled={!refinementInstruction.trim() || isRefining}
            startIcon={isRefining ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
          >
            {isRefining ? 'Refining...' : 'Refine'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrops */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1400,
          flexDirection: 'column'
        }}
        open={isRefining}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>Refining content with AI...</Typography>
      </Backdrop>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1400,
          flexDirection: 'column'
        }}
        open={isPrinting}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>Preparing professional print layout...</Typography>
      </Backdrop>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete Report</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {isLoadingPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading deletion details...
                </Typography>
              </Box>
            ) : deletePreview ? (
              <>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  ⚠️ This action cannot be undone!
                </Alert>
                
                {deletePreview.warningMessage && (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    {deletePreview.warningMessage}
                  </Alert>
                )}
                
                <Box>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    {deletePreview.reportTitle || report.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Report ID: {reportId}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="error">
                    What will be deleted:
                  </Typography>
                  
                  {deletePreview.versions.map((version) => (
                    <Box 
                      key={version.versionNumber}
                      sx={{ 
                        p: 1.5, 
                        mb: 1,
                        borderRadius: 1,
                        backgroundColor: version.isActive 
                          ? 'rgba(255, 152, 0, 0.1)' 
                          : 'rgba(0, 0, 0, 0.03)',
                        border: version.isActive 
                          ? '1px solid rgba(255, 152, 0, 0.3)'
                          : '1px solid rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600}>
                          Version {version.versionNumber}
                        </Typography>
                        {version.isActive && (
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="warning"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {version.extractionCount} extracted graph{version.extractionCount !== 1 ? 's' : ''}
                      </Typography>
                      {version.changeDescription && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {version.changeDescription}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  
                  <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(211, 47, 47, 0.08)', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} color="error">
                      Total Impact:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {deletePreview.willDeleteVersions} version{deletePreview.willDeleteVersions !== 1 ? 's' : ''} will be removed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • {deletePreview.totalExtractions} extracted graph{deletePreview.totalExtractions !== 1 ? 's' : ''} will be lost
                    </Typography>
                    {deletePreview.versions.some(v => v.isActive) && (
                      <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                        ℹ️ Another version will be auto-activated after deletion
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                Are you sure you want to delete this report? This action cannot be undone.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteReport}
            disabled={isDeleting || isLoadingPreview}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar 
        open={alertState.open} 
        autoHideDuration={4000} 
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alertState.severity} 
          sx={{ width: '100%' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
      
      {/* Coming Soon Notification */}
      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={3000}
        onClose={() => setComingSoonOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setComingSoonOpen(false)} 
          severity="info"
          sx={{
            background: 'linear-gradient(135deg, #FF9500 0%, #FF6B00 100%)',
            color: '#fff',
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(255,149,0,0.4)',
            '& .MuiAlert-icon': {
              color: '#fff',
            },
          }}
        >
          {comingSoonFeature} feature coming soon! 🚀
        </Alert>
      </Snackbar>

      {/* Clinical Guidelines Sidebar - Only visible in Edit Mode */}
      {isEditing && (
        <GuidelinesSidebar
          open={isGuidelinesSidebarOpen}
          onClose={() => setIsGuidelinesSidebarOpen(false)}
        />
      )}

      {/* AI Prompts Sidebar - Only visible in Edit Mode */}
      {isEditing && (
        <PromptsSidebar
          open={isPromptsSidebarOpen}
          onClose={() => setIsPromptsSidebarOpen(false)}
          onSelectPrompt={(prompt) => {
            setChatMessage(prompt);
          }}
        />
      )}

      {/* Image Viewer Modal */}
      <Dialog
        open={imageViewerOpen}
        onClose={handleCloseImageViewer}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
            width: '95vw',
            height: '95vh',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 0,
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header with controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              {selectedImage?.label || 'Image Viewer'}
            </Typography>
            
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {selectedImageIndex + 1} / {report.images.length}
              </Typography>
              
              <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
              
              <Tooltip title="Zoom Out" arrow>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={imageZoom <= 0.5}
                  sx={{ color: 'white' }}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              
              <Typography variant="body2" sx={{ color: 'white', minWidth: 60, textAlign: 'center' }}>
                {Math.round(imageZoom * 100)}%
              </Typography>
              
              <Tooltip title="Zoom In" arrow>
                <IconButton
                  onClick={handleZoomIn}
                  disabled={imageZoom >= 5}
                  sx={{ color: 'white' }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Reset Zoom" arrow>
                <IconButton
                  onClick={handleResetZoom}
                  sx={{ color: 'white' }}
                >
                  <ZoomOutMapIcon />
                </IconButton>
              </Tooltip>
              
              <Divider orientation="vertical" flexItem sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
              
              <Tooltip title="Open in New Window" arrow>
                <IconButton
                  onClick={handleDownloadImage}
                  sx={{ color: 'white' }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Close" arrow>
                <IconButton
                  onClick={handleCloseImageViewer}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Navigation arrows */}
          {report.images.length > 1 && (
            <>
              {selectedImageIndex > 0 && (
                <IconButton
                  onClick={handlePreviousImage}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    zIndex: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 32, fontWeight: 'bold' }}>‹</Typography>
                </IconButton>
              )}
              
              {selectedImageIndex < report.images.length - 1 && (
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    zIndex: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 32, fontWeight: 'bold' }}>›</Typography>
                </IconButton>
              )}
            </>
          )}

          {/* Image display area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              userSelect: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.label}
                style={{
                  maxWidth: imageZoom === 1 ? '100%' : 'none',
                  maxHeight: imageZoom === 1 ? '100%' : 'none',
                  transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease',
                  objectFit: 'contain',
                }}
                draggable={false}
              />
            )}
          </Box>

          {/* Bottom info bar */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Click and drag to pan when zoomed • Use mouse wheel or buttons to zoom • Arrow keys to navigate • Press D to open in new window
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ReportPreview;